import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  makeStyles,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppButton from "components/CustomButtons/AppButton";
import AppColor from "utilities/AppColor";
import { RISK_SEVERITY, RISK_STATUS, CONSENT_STATUS } from "config/AppConfig";
import { fetchRisksForClient, fetchConsentsForClient, deleteConsent } from "store/slices/client";
import { getAuthInfo } from "store/slices/auth";
import AddRiskModal from "../form/AddRiskModal";
import AddConsentModal from "../form/AddConsentModal";
import AppConfirmDialog from "components/Dialog/AppConfirmDialog";
import moment from "moment";
import PreviewDialog from "components/Dialog/PreviewDialog";

const useStyles = makeStyles(() => ({
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "#0D9488",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 12,
    marginBottom: 24,
  },
  tableHead: {
    background: "#FAFAFA",
  },
  tableCellHead: {
    padding: "10px 12px",
    textAlign: "left",
    fontWeight: 600,
    color: "#6B7280",
    borderBottom: "1px solid #E5E7EB",
    fontSize: 11,
  },
  tableCellBody: {
    padding: "12px",
    borderBottom: "1px solid #F3F4F6",
    color: "#111827",
    fontSize: 12,
    verticalAlign: "middle",
  },
  controlsCell: {
    maxWidth: "150px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  pill: {
    padding: "2px 10px",
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 600,
    display: "inline-block",
    border: "1px solid transparent",
  },
  loadingBox: {
    padding: "30px 0",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 10,
    color: "#6B7280",
    fontSize: 13,
  },
  emptyRow: {
    textAlign: "center",
    color: "#9CA3AF",
    padding: "28px 0",
    fontSize: 13,
  },
  editIcon: {
    color: "#6B7280",
    fontSize: 14,
    cursor: "pointer",
    "&:hover": {
      color: AppColor.mainColor,
    },
  },
}));

// ── Colour helpers ────────────────────────────────────────────────────────────

const RATING_STYLES = {
  [RISK_SEVERITY.EXTREME]: { bg: "#FEE2E2", color: "#991B1B", border: "#FCA5A5" },
  [RISK_SEVERITY.HIGH]: { bg: "#FFEDD5", color: "#9A3412", border: "#FED7AA" },
  [RISK_SEVERITY.MEDIUM]: { bg: "#FEF9C3", color: "#854D0E", border: "#FDE68A" },
  [RISK_SEVERITY.LOW]: { bg: "#D1FAE5", color: "#065F46", border: "#6EE7B7" },
};

const SEVERITY_STYLES = {
  [RISK_SEVERITY.EXTREME]: { bg: "#FEE2E2", color: "#991B1B" },
  [RISK_SEVERITY.HIGH]: { bg: "#FFEDD5", color: "#9A3412" },
  [RISK_SEVERITY.MEDIUM]: { bg: "#DBEAFE", color: "#1E40AF" },
  [RISK_SEVERITY.LOW]: { bg: "#D1FAE5", color: "#065F46" },
};

const STATUS_STYLES = {
  [RISK_STATUS.ACTIVE]: { bg: "#CCFBF1", color: "#0D9488" },
  [RISK_STATUS.MONITORING]: { bg: "#EDE9FE", color: "#5B21B6" },
  [RISK_STATUS.RESOLVED]: { bg: "#F3F4F6", color: "#6B7280" },
};

const CONSENT_STATUS_STYLES = {
  [CONSENT_STATUS.PENDING]: { bg: "#FFE4E6", color: "#BE123C" },
  [CONSENT_STATUS.SIGNED]: { bg: "#CCFBF1", color: "#0D9488" },
  [CONSENT_STATUS.VERBAL]: { bg: "#EDE9FE", color: "#5B21B6" },
};

// ── Component ─────────────────────────────────────────────────────────────────

const RiskConsentTab = ({ participantName }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { id: clientId } = useParams();
  const authInfo = useSelector(getAuthInfo);

  // Risk state
  const [risks, setRisks] = useState([]);
  const [loadingRisks, setLoadingRisks] = useState(false);
  const [showAddRiskModal, setShowAddRiskModal] = useState(false);
  const [editRiskData, setEditRiskData] = useState(null);

  // Consent state
  const [consents, setConsents] = useState([]);
  const [loadingConsents, setLoadingConsents] = useState(false);
  const [showAddConsentModal, setShowAddConsentModal] = useState(false);
  const [editConsentData, setEditConsentData] = useState(null);
  const [showDeleteConsentModal, setShowDeleteConsentModal] = useState(false);
  const [consentToDelete, setConsentToDelete] = useState(null);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);


  // Fetch data
  const loadRisks = useCallback(async () => {
    if (!clientId) return;
    setLoadingRisks(true);
    const result = await dispatch(fetchRisksForClient({ clientId }));
    if (result.payload) setRisks(result.payload);
    setLoadingRisks(false);
  }, [dispatch, clientId]);

  const loadConsents = useCallback(async () => {
    if (!clientId) return;
    setLoadingConsents(true);
    const result = await dispatch(fetchConsentsForClient({ clientId }));
    if (result.payload) setConsents(result.payload);
    setLoadingConsents(false);
  }, [dispatch, clientId]);

  useEffect(() => {
    loadRisks();
    loadConsents();
  }, [loadRisks, loadConsents]);

  // ── Render helpers ──────────────────────────────────────────────────────────

  const renderPill = (value, stylesMap) => {
    if (!value) return "-";
    const s = stylesMap[value] || { bg: "#F3F4F6", color: "#6B7280" };
    return (
      <span className={classes.pill} style={{ background: s.bg, color: s.color, borderColor: s.border || "transparent" }}>
        {t(`riskRating.${value}`) || t(`riskSeverity.${value}`) || t(`riskStatus.${value}`) || value}
      </span>
    );
  };

  const renderRatingPill = (rating) => {
    if (!rating) return "-";
    const s = RATING_STYLES[rating] || {};
    return (
      <span className={classes.pill} style={{ background: s.bg, color: s.color, borderColor: s.border }}>
        {t(`riskRating.${rating}`)}
      </span>
    );
  };

  const renderSeverityPill = (severity) => {
    if (!severity) return "-";
    const s = SEVERITY_STYLES[severity] || {};
    return (
      <span className={classes.pill} style={{ background: s.bg, color: s.color }}>
        {t(`riskSeverity.${severity}`)}
      </span>
    );
  };

  const renderRiskStatusPill = (status) => {
    if (!status) return "-";
    const s = STATUS_STYLES[status] || {};
    return (
      <span className={classes.pill} style={{ background: s.bg, color: s.color }}>
        {t(`riskStatus.${status}`)}
      </span>
    );
  };

  const renderConsentStatusPill = (status) => {
    if (!status) return "-";
    const s = CONSENT_STATUS_STYLES[status] || {};
    return (
      <span className={classes.pill} style={{ background: s.bg, color: s.color }}>
        {t(`consentStatus.${status}`)}
      </span>
    );
  };

  const formatDate = (d) => (d ? moment(d).format("D MMM YYYY") : "-");

  // ── Risk modal handlers ────────────────────────────────────────────────────

  const handleAddRisk = () => {
    setEditRiskData(null);
    setShowAddRiskModal(true);
  };

  const handleEditRisk = (risk) => {
    setEditRiskData(risk);
    setShowAddRiskModal(true);
  };

  const handleCloseRiskModal = () => {
    setShowAddRiskModal(false);
    setEditRiskData(null);
  };

  // ── Consent modal handlers ─────────────────────────────────────────────────

  const handleAddConsent = () => {
    setEditConsentData(null);
    setShowAddConsentModal(true);
  };

  const handleEditConsent = (consent) => {
    setEditConsentData(consent);
    setShowAddConsentModal(true);
  };

  const handleCloseConsentModal = () => {
    setShowAddConsentModal(false);
    setEditConsentData(null);
  };

  const handleDeleteConsent = (consent) => {
    setConsentToDelete(consent);
    setShowDeleteConsentModal(true);
  };

  const confirmDeleteConsent = async () => {
    if (!consentToDelete) return;
    await dispatch(deleteConsent({ id: consentToDelete.id }));
    setShowDeleteConsentModal(false);
    setConsentToDelete(null);
    loadConsents();
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* ── RISK REGISTER SECTION ── */}
      <Box className={classes.sectionHeader}>
        <Typography className={classes.sectionTitle}>{t("riskConsent.riskRegister")}</Typography>
        <AppButton
          label={t("riskConsent.addRisk")}
          buttonColor={AppColor.mainColor}
          mode="outlined"
          rounder
          style={{ fontSize: 11, textTransform: "none" }}
          startIcon={<i className="fas fa-plus" style={{ fontSize: 10 }} />}
          onClick={handleAddRisk}
        />
      </Box>

      {loadingRisks ? (
        <Box className={classes.loadingBox}>
          <CircularProgress size={18} style={{ color: AppColor.mainColor }} />
          {t("label.loading")}
        </Box>
      ) : (
        <Table className={classes.table}>
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell className={classes.tableCellHead} style={{ width: "22%" }}>{t("riskConsent.riskName")}</TableCell>
              <TableCell className={classes.tableCellHead} style={{ width: "12%" }}>{t("riskConsent.riskCategory")}</TableCell>
              <TableCell className={classes.tableCellHead} style={{ width: "10%" }}>{t("riskConsent.severity")}</TableCell>
              {/* <TableCell className={classes.tableCellHead} style={{ width: "10%" }}>{t("riskConsent.rating")}</TableCell>
              <TableCell className={classes.tableCellHead} style={{ width: "10%" }}>{t("riskConsent.status")}</TableCell> */}
              <TableCell className={classes.tableCellHead} style={{ width: "18%" }}>{t("riskConsent.controls")}</TableCell>
              <TableCell className={classes.tableCellHead} style={{ width: "10%" }}>{t("riskConsent.review")}</TableCell>
              <TableCell className={classes.tableCellHead} style={{ width: "8%" }}>{t("riskConsent.action")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {risks.length > 0 ? (
              risks.map((row, index) => (
                <TableRow key={row.id || index}>
                  <TableCell className={classes.tableCellBody}>
                    <Box fontWeight={600}>{row.name}</Box>
                    {/* {row.description && (
                      <Box style={{ color: "#6B7280", fontSize: 11, marginTop: 2 }}>
                        {row.description}
                      </Box>
                    )} */}
                  </TableCell>
                  <TableCell className={classes.tableCellBody}>{row.category ? t(`riskCategory.${row.category}`) : "-"}</TableCell>
                  <TableCell className={classes.tableCellBody}>{renderSeverityPill(row.severity)}</TableCell>
                  {/* <TableCell className={classes.tableCellBody}>{renderRatingPill(row.rating)}</TableCell>
                  <TableCell className={classes.tableCellBody}>{renderRiskStatusPill(row.status)}</TableCell> */}
                  <TableCell className={`${classes.tableCellBody} ${classes.controlsCell}`} title={row.currentControls || "-"}>{row.currentControls || "-"}</TableCell>
                  <TableCell className={classes.tableCellBody}>{row.reviewDate ? formatDate(row.reviewDate) : "-"}</TableCell>
                  <TableCell className={classes.tableCellBody}>
                    <Tooltip title={t("riskConsent.editRisk")}>
                      <IconButton size="small" onClick={() => handleEditRisk(row)}>
                        <i className={`fas fa-edit ${classes.editIcon}`} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} className={classes.tableCellBody}>
                  <Box className={classes.emptyRow}>{t("riskConsent.noRisks")}</Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      {/* ── CONSENT REGISTER SECTION ── */}
      <Box className={classes.sectionHeader}>
        <Typography className={classes.sectionTitle}>{t("riskConsent.consentRegister")}</Typography>
        <AppButton
          label={t("riskConsent.addConsent")}
          buttonColor={AppColor.mainColor}
          mode="outlined"
          rounder
          style={{ fontSize: 11, textTransform: "none" }}
          startIcon={<i className="fas fa-plus" style={{ fontSize: 10 }} />}
          onClick={handleAddConsent}
        />
      </Box>

      {loadingConsents ? (
        <Box className={classes.loadingBox}>
          <CircularProgress size={18} style={{ color: AppColor.mainColor }} />
          {t("label.loading")}
        </Box>
      ) : (
        <Table className={classes.table}>
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell className={classes.tableCellHead} style={{ width: "30%" }}>{t("riskConsent.name")}</TableCell>
              <TableCell className={classes.tableCellHead} style={{ width: "15%" }}>{t("riskConsent.status")}</TableCell>
              <TableCell className={classes.tableCellHead} style={{ width: "15%" }}>{t("riskConsent.date")}</TableCell>
              <TableCell className={classes.tableCellHead} style={{ width: "30%" }}>{t("riskConsent.document")}</TableCell>
              <TableCell className={classes.tableCellHead} style={{ width: "10%" }}>{t("riskConsent.action")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {consents.length > 0 ? (
              consents.map((row, index) => (
                <TableRow key={row.id || index}>
                  <TableCell className={classes.tableCellBody}>{row.name}</TableCell>
                  <TableCell className={classes.tableCellBody}>{renderConsentStatusPill(row.status)}</TableCell>
                  <TableCell className={classes.tableCellBody}>{row.reviewDate ? formatDate(row.reviewDate) : "-"}</TableCell>
                  <TableCell className={classes.tableCellBody} onClick={() => {
                    if (row.documentUrl) {
                      setPdfUrl(row.documentUrl)
                      setShowPdfModal(true)
                    }
                  }} style={{ cursor: "pointer" }}>
                    {row.documentUrl ? (
                      <span style={{ color: "#0D9488", fontSize: 12 }}>
                        <i className="fas fa-file-pdf" style={{ marginRight: 4 }} />
                        {t("riskConsent.viewDocument")}
                      </span>
                    ) : row.verbalConsentRecorded ? (
                      <span style={{ color: "#5B21B6", fontSize: 12 }}>
                        <i className="fas fa-microphone" style={{ marginRight: 4 }} />
                        {t("riskConsent.verbalRecorded")}
                        {row.verbalConsentMethod && ` — ${row.verbalConsentMethod}`}
                      </span>
                    ) : (
                      <span style={{ color: "#9CA3AF", fontSize: 12 }}>{t("riskConsent.noDocument")}</span>
                    )}
                  </TableCell>
                  <TableCell className={classes.tableCellBody}>
                    {row.documentUrl &&
                      <Tooltip title={t("button.download")}>
                        <a href={row.documentUrl} download={row.documentUrl} style={{ display: 'contents' }}>
                          <IconButton size="small">
                            <i className="fas fa-download" style={{ color: "#0D9488", fontSize: 14, cursor: "pointer" }} />
                          </IconButton>
                        </a>
                      </Tooltip>}
                    <Tooltip title={t("riskConsent.editConsent")}>
                      <IconButton size="small" onClick={() => handleEditConsent(row)}>
                        <i className={`fas fa-edit ${classes.editIcon}`} />
                      </IconButton>
                    </Tooltip>
                    {
                      !row.default &&
                      <Tooltip title={t("riskConsent.deleteConsent")}>
                        <IconButton size="small" onClick={() => handleDeleteConsent(row)} >
                          <i className="fas fa-trash" style={{ color: "#EF4444", fontSize: 14, cursor: "pointer" }} />
                        </IconButton>
                      </Tooltip>
                    }
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} className={classes.tableCellBody}>
                  <Box className={classes.emptyRow}>{t("riskConsent.noConsents")}</Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      {/* ── Modals ── */}
      <AddRiskModal
        open={showAddRiskModal}
        onClose={handleCloseRiskModal}
        clientId={clientId ? Number(clientId) : null}
        companyId={authInfo?.companyId}
        participantName={participantName}
        onSuccess={loadRisks}
        initialData={editRiskData}
      />

      <AddConsentModal
        open={showAddConsentModal}
        onClose={handleCloseConsentModal}
        clientId={clientId ? Number(clientId) : null}
        companyId={authInfo?.companyId}
        onSuccess={loadConsents}
        initialData={editConsentData}
      />

      <AppConfirmDialog
        open={showDeleteConsentModal}
        onConfirm={confirmDeleteConsent}
        onClose={() => { setShowDeleteConsentModal(false); setConsentToDelete(null); }}
        title={t("riskConsent.deleteConsent")}
        content={t("riskConsent.confirmDeleteConsent")}
      />
      <PreviewDialog open={showPdfModal} setOpen={setShowPdfModal} fileUrl={pdfUrl} pdfUrl={pdfUrl} />

    </Box>
  );
};

export default RiskConsentTab;
